import React, { useState, useEffect, useRef } from 'react';
import { ConfigProvider, theme } from 'antd';
import Sidebar from './components/Sidebar.jsx';
import ChatInterface from './components/ChatInterface.jsx';
import ProjectDropdown from './components/ProjectDropdown.jsx';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import LoginForm from './components/LoginForm.jsx';
import ComplianceIntro from './components/ComplianceIntro.jsx';
import './styles/documentManagement.css';

const AppContent = () => {
  const { isAuthenticated, hasComplianceAccess, loading, checkingAccess } = useAuth();
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [sidebarUpdateFunction, setSidebarUpdateFunction] = useState(null);
  const sidebarRefreshFunctionRef = useRef(null);

  const handleNewChat = () => {
    setCurrentSessionId(null);
  };

  const handleSelectSession = (sessionId) => {
    setCurrentSessionId(sessionId);
  };

  const handleSessionChange = (sessionId) => {
    setCurrentSessionId(sessionId);
  };

  const handleProjectChange = (project) => {
    setCurrentProject(project);
    // Clear current session when switching projects to avoid cross-project confusion
    setCurrentSessionId(null);
  };

  const handleSidebarUpdate = (updateFunction) => {
    setSidebarUpdateFunction(() => updateFunction);
  };

  const handleSidebarRefreshCallback = (refreshFunction) => {
    console.log('App: Received sidebar refresh callback, setting function via ref');
    sidebarRefreshFunctionRef.current = refreshFunction;
  };

  const handleSidebarRefresh = () => {
    console.log('App: handleSidebarRefresh called, function available:', !!sidebarRefreshFunctionRef.current);
    if (sidebarRefreshFunctionRef.current) {
      console.log('App: Calling sidebarRefreshFunction');
      sidebarRefreshFunctionRef.current();
    } else {
      console.warn('App: sidebarRefreshFunction not available yet');
    }
  };

  const handleSessionUpdate = (sessionId, newName) => {
    if (sidebarUpdateFunction) {
      sidebarUpdateFunction(sessionId, newName);
    }
  };

  if (loading || checkingAccess) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  if (!hasComplianceAccess) {
    return <ComplianceIntro />;
  }

  return (
    <div className="compliance-app">
      <Sidebar 
        onNewChat={handleNewChat}
        currentSessionId={currentSessionId}
        onSelectSession={handleSelectSession}
        currentProject={currentProject}
        onProjectChange={handleProjectChange}
        onSessionUpdate={handleSidebarUpdate}
        onSidebarRefreshCallback={handleSidebarRefreshCallback}
      />
      <main className="main-content">
        <ChatInterface 
          sessionId={currentSessionId}
          onSessionChange={handleSessionChange}
          currentProject={currentProject}
          onSessionUpdate={handleSessionUpdate}
          onSidebarRefresh={handleSidebarRefresh}
        />
      </main>
    </div>
  );
};

const App = () => {
  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#8b5cf6',
          colorBgBase: '#0a0a0b',
          colorBgContainer: '#111111',
          colorBgElevated: '#1a1a1b',
          colorText: '#ffffff',
          colorTextSecondary: '#e5e5e5',
          colorBorder: '#262626',
          colorBorderSecondary: '#333333',
        },
        components: {
          Tabs: {
            colorText: '#ffffff',
            colorTextDisabled: '#666666',
            colorPrimary: '#8b5cf6',
            colorPrimaryHover: '#a855f7',
            colorBgContainer: 'transparent',
          },
        },
      }}
    >
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;